/**
 * Enhanced Conversation Management Page
 *
 * Real-time conversation management for company owners with modern features:
 * - Real-time WebSocket updates (no more manual refresh!)
 * - Professional split view (40% list, 60% detail)
 * - Enhanced customer tracking with device/location/marketing data
 * - Live conversation updates and agent assignment workflow
 * - Comprehensive customer profiles and analytics
 * - Smart connection status indicators
 * - Simplified 3-status system
 */

import React, { useEffect, useState, useCallback } from 'react'
import { api } from '../../services/api'
import { Card, CardContent } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import {
  Zap,
  WifiOff,
  Wifi,
  AlertTriangle
} from 'lucide-react'
import { useRealTimeNotifications } from '../../hooks/useRealTimeNotifications'
import WebSocketDiagnostic from '../../components/debug/WebSocketDiagnostic'
import ProfessionalConversationView from '../../components/features/chat/professional/ProfessionalConversationView'

// Enhanced Status System - Simplified 3-status approach
const CONVERSATION_STATUS = {
  PENDING: 'pending',
  UNRESOLVED: 'unresolved',
  RESOLVED: 'resolved'
} as const

const STATUS_COLORS = {
  pending: {
    primary: '#FF8C00',
    light: '#FFE4B5',
    dark: '#FF6347',
    text: '#FFFFFF'
  },
  unresolved: {
    primary: '#32CD32',
    light: '#F0FFF0',
    dark: '#228B22',
    text: '#FFFFFF'
  },
  resolved: {
    primary: '#9370DB',
    light: '#E6E6FA',
    dark: '#4B0082',
    text: '#FFFFFF'
  }
}

const PRIORITY_LEVELS = {
  URGENT: 'urgent',
  HIGH: 'high',
  NORMAL: 'normal',
  LOW: 'low'
} as const

const mapLegacyToNewStatus = (legacyStatus: string, hasAgentResponse: boolean = false): string => {
  switch (legacyStatus) {
    case 'ai_active':
      return hasAgentResponse ? CONVERSATION_STATUS.UNRESOLVED : CONVERSATION_STATUS.PENDING
    case 'human_assigned':
    case 'human_active':
      return CONVERSATION_STATUS.UNRESOLVED
    case 'resolved':
    case 'archived':
      return CONVERSATION_STATUS.RESOLVED
    default:
      return CONVERSATION_STATUS.PENDING
  }
}

const calculatePriority = (conversation: any): string => {
  const now = new Date()
  const lastMessageTime = new Date(conversation.last_message_at || conversation.created_at)
  const waitTimeMinutes = (now.getTime() - lastMessageTime.getTime()) / (1000 * 60)

  const isVip = conversation.visitor_name?.toLowerCase().includes('vip') || false
  const hasUrgentKeywords = conversation.last_message?.toLowerCase().includes('urgent') ||
                           conversation.last_message?.toLowerCase().includes('emergency') || false

  if (isVip || hasUrgentKeywords || waitTimeMinutes > 30) {
    return PRIORITY_LEVELS.URGENT
  } else if (waitTimeMinutes > 15 || (conversation.message_count || 0) > 10) {
    return PRIORITY_LEVELS.HIGH
  } else if (waitTimeMinutes < 5) {
    return PRIORITY_LEVELS.LOW
  } else {
    return PRIORITY_LEVELS.NORMAL
  }
}

const StatusBadge: React.FC<{ status: string; priority: string; className?: string }> = ({
  status,
  priority,
  className = ''
}) => {
  const colors = STATUS_COLORS[status as keyof typeof STATUS_COLORS] || STATUS_COLORS.pending
  const icons = {
    pending: '🟠',
    unresolved: '🟢',
    resolved: '🟣'
  }

  const priorityIcon = priority === PRIORITY_LEVELS.URGENT ? '🔥' :
                      priority === PRIORITY_LEVELS.HIGH ? '⚡' : ''

  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${className}`}
      style={{
        backgroundColor: colors.primary,
        color: colors.text
      }}
    >
      <span>{icons[status as keyof typeof icons]}</span>
      <span>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
      {priorityIcon && <span className="ml-1">{priorityIcon}</span>}
    </div>
  )
}

interface ConversationStats {
  active_conversations: number
  pending_handoffs: number
  available_agents: number
  total_agents: number
  agents_ratio: string
  avg_response_time_minutes: number
  ai_resolution_rate: number
  last_updated: string
  pending_count?: number
  unresolved_count?: number
  resolved_count?: number
  urgent_count?: number
}

export const ConversationManagement: React.FC = () => {
  const [, setStats] = useState<ConversationStats | null>(null)
  const [, setIsLoadingStats] = useState(true)
  const [lastStatsUpdate, setLastStatsUpdate] = useState<Date>(new Date())

  const {
    isConnected: isRealTimeConnected,
    connectionState,
    lastUpdate: realTimeLastUpdate
  } = useRealTimeNotifications()

  useEffect(() => {
    console.log('ConversationManagement page mounted')
    fetchStats()
  }, [])

  const fetchStats = useCallback(async () => {
    try {
      setIsLoadingStats(true)
      const response = await api.get<ConversationStats>('/conversations/stats')
      setStats(response.data)
      setLastStatsUpdate(new Date())
    } catch (error) {
      console.error('Failed to load conversation stats:', error)
      setStats({
        active_conversations: 0,
        pending_handoffs: 0,
        available_agents: 0,
        total_agents: 0,
        agents_ratio: '0/0',
        avg_response_time_minutes: 0,
        ai_resolution_rate: 85,
        last_updated: new Date().toISOString()
      })
    } finally {
      setIsLoadingStats(false)
    }
  }, [])

  const getConnectionStatus = () => {
    if (isRealTimeConnected) {
      return {
        icon: <Zap className="w-3 h-3" />,
        label: 'Live',
        className: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20'
      }
    }

    if (connectionState === 'connecting') {
      return {
        icon: <Wifi className="w-3 h-3 animate-pulse" />,
        label: 'Connecting',
        className: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20'
      }
    }

    return {
      icon: <WifiOff className="w-3 h-3" />,
      label: 'Offline',
      className: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20'
    }
  }

  const connectionStatus = getConnectionStatus()

  return (
    <div className="space-y-4 w-full max-w-none p-0 m-0">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-foreground">Conversations</h1>

            {/* Connection status badge */}
            <Badge
              variant="outline"
              className={connectionStatus.className}
            >
              {connectionStatus.icon}
              <span className="ml-1">{connectionStatus.label}</span>
            </Badge>
          </div>

          <div className="flex items-center gap-4">
            <p className="text-sm text-muted-foreground">
              Real-time conversation management with customer intelligence.
            </p>

            {(realTimeLastUpdate || lastStatsUpdate) && (
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                Updated {(realTimeLastUpdate || lastStatsUpdate).toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {!isRealTimeConnected && (
            <Badge variant="outline" className="bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/20">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Reconnecting...
            </Badge>
          )}
        </div>
      </div>

      {/* Professional Conversation Management Interface */}
      <div className="h-[calc(100vh-12rem)] min-h-[600px] w-full">
        <Card className="h-full overflow-hidden border-0 shadow-none w-full">
          <CardContent className="h-full p-0">
            <ProfessionalConversationView
              className="h-full"
              isRealTimeConnected={isRealTimeConnected}
              realTimeConnectionState={connectionState}
              lastRealTimeUpdate={realTimeLastUpdate}
              debugMode={process.env.NODE_ENV === 'development'}
              conversationStatus={CONVERSATION_STATUS}
              statusColors={STATUS_COLORS}
              priorityLevels={PRIORITY_LEVELS}
              mapLegacyToNewStatus={mapLegacyToNewStatus}
              calculatePriority={calculatePriority}
              StatusBadge={StatusBadge}
            />
          </CardContent>
        </Card>
      </div>

      {/* Dev diagnostics */}
      {process.env.NODE_ENV === 'development' && <WebSocketDiagnostic />}
    </div>
  )
}

export default ConversationManagement
